import type { NextApiRequest, NextApiResponse } from 'next'
import { useLocaleCredentials as setLocaleCredentials } from 'api'
import storefrontApi from 'api/storefront-api'
import { getCountryCodeFromPaymentConfig } from 'api/utils/payment/getCountryCodeFromPaymentConfig'
import { CustomerType, type Language } from 'ecosystem'
import { buyerVatRatesFor, createLogger, DEFAULT_COUNTRY } from 'shared-utils'
import { handlerWithLogging } from '../../../logging'
import { type CreateOrderHandlerOpts } from './types'
import { klarnaPaymentProvider } from './klarnaPaymentProvider'
import { nexiPaymentProvider } from './nexiPaymentProvider'
import { sveaPaymentProvider } from './sveaPaymentProvider'
import { qliroPaymentProvider } from './qliroPaymentProvider'
import { briqpayPaymentProvider } from './briqpayPaymentProvider'

const logger = createLogger('createOrderHandler')

/**
 * Resolve the buyer's VAT treatment and business/consumer classification once
 * per request, so every provider branch below prices service and shipping rows
 * on the same basis the cart lines were built on. A lookup failure must not
 * block checkout — we fall back to the store's own rate, which is what happens
 * today, and leave `customerType` unknown so no delivery rule keys off a guess.
 *
 * The same lookup decides whether `customerId` is safe to stamp on the order.
 * The BE rejects a create whose `customerId` does not resolve to an *active*
 * Customer in the store, and a rejected create is a broken checkout — so the id
 * is carried forward only when this lookup proves it good. Anything else (an
 * unknown id, a deactivated account, a failed call) falls back to the
 * pre-existing guest behaviour: the order links by billing email at
 * provisioning instead.
 */
const resolveBuyer = async (
  opts: CreateOrderHandlerOpts
): Promise<Pick<CreateOrderHandlerOpts, 'buyerVat' | 'customerType' | 'customerId'>> => {
  // An anonymous shopper is a consumer by definition.
  if (!opts.customerId) {
    return { buyerVat: null, customerType: CustomerType.CONSUMER, customerId: null }
  }

  try {
    const customer = await storefrontApi.customer.findById(opts.customerId)
    const tenantCountry =
      getCountryCodeFromPaymentConfig(opts.paymentProviderConfig) ?? DEFAULT_COUNTRY

    if (!customer?.active) {
      logger.error(
        `Session customer ${opts.customerId} is not active; placing the order without an identity`
      )
    }

    return {
      buyerVat: buyerVatRatesFor(customer?.companyProfile, tenantCountry),
      customerType: customer?.type,
      customerId: customer?.active ? customer.id : null
    }
  } catch (e) {
    logger.error('Failed to resolve the buyer; falling back to store rate', e)
    return { buyerVat: null, customerId: null }
  }
}

export const createOrderHandler = handlerWithLogging(
  async (req: NextApiRequest, res: NextApiResponse, opts: CreateOrderHandlerOpts) => {
    if (req.method !== 'POST') {
      res.status(405).json({ error: 'Method not supported.' })
      return
    }

    const locale = (req.cookies.NEXT_LOCALE as Language) || 'sv'
    setLocaleCredentials(locale)

    const { paymentProviderConfig } = opts
    // Also narrows `customerId` to one the BE will accept — see `resolveBuyer`.
    const optsWithVat: CreateOrderHandlerOpts = { ...opts, ...(await resolveBuyer(opts)) }

    try {
      switch (paymentProviderConfig?.providerName ?? 'KLARNA') {
        case 'KLARNA':
          return klarnaPaymentProvider(req, res, optsWithVat)
        case 'NEXI':
          return nexiPaymentProvider(req, res, optsWithVat)
        case 'SVEA': {
          return sveaPaymentProvider(req, res, optsWithVat)
        }
        case 'QLIRO':
          return qliroPaymentProvider(req, res, optsWithVat)
        case 'BRIQPAY':
          return briqpayPaymentProvider(req, res, optsWithVat)
        // todo: add LEDYER
        case 'LEDYER': {
          throw new Error('Not implemented yet: "LEDYER" case')
        }
      }
    } catch (e) {
      res.status(400).json({ error: e instanceof Error ? e.message : 'Unknown error' })
    }
  }
)
